import { useContext, useRef } from 'react';
import { Form, Formik } from 'formik';
import { array, number, object, string } from 'yup';

import {
  CustomTag,
  CustomTagBottle,
  CustomTagHalfBottle,
  NextLink,
} from '@/atoms';
import { Button, Checkbox, Input, Modal, Radio, StepForm } from '@/molecules';

import { CustomTag as CustomTagContext } from '@/components/providers/CustomTag';
import { WORDPRESS_QUERY } from '@/lib/graphql/enums';
import { useQuery } from '@/lib/hooks';
import { routes } from '@/lib/routes';
import {
  censureOfensiveWords,
  debug,
  FORMS_ERROS,
  FORMS_ERROS as GENERIC_FORMS_ERRORS,
  isEmptyObject,
} from '@/lib/utils';

import type { OffensiveWords } from '@/lib/graphql/querys';
import type { FunctionComponent } from 'react';
import type { TypeCustomTagModalProps } from './types';

const IDS_FORM = {
  step: 'customTagStep',
  numlines: 'customTagNumLines',
  lineOne: 'customTagLineOne',
  lineTwo: 'customTagLineTwo',
  lineThree: 'customTagLineThree',
  lineOptional: 'customTagLineOptional',
  terms: 'customTagCheckboxTerms',
};

const DEFAULT_VALUES = {
  lineOne: 'Línea 1',
  lineTwo: 'Línea 2',
  lineThree: 'Línea 3',
  lineOptional: 'Texto inferior',
  terms: 'accept-terms',
};

const FORM_ERRORS = {
  ofensiveWord: 'Texto no admitido. Consultar bases legales.',
};

/**
 * CustomTagModal
 */
export const CustomTagModal: FunctionComponent<TypeCustomTagModalProps> = ({
  open,
  setOpen,
  background,
}: TypeCustomTagModalProps) => {
  /**
   * ¡¡¡Is IMPORTANT initialize all values!!!
   * Otherwise Formik will not touch the fields when submitting and they will not show the error
   */
  // TODO: añadir tipos
  const { data, loading } = useQuery<OffensiveWords>(
    WORDPRESS_QUERY.OFFENSIVE_WORDS,
  );
  const words = data?.bcGeneralSettings.ajustesGenerales.palabrasOfensivas;
  const offensiveWords = words
    ?.replace(/'/g, '')
    ?.replace(/[\r\n]/gm, '')
    ?.split(',');
  const initialValues: any = {
    customTagStep: 1,
    customTagNumLines: 1,
    customTagLineOne: '',
    customTagLineTwo: '',
    customTagLineThree: '',
    customTagLineOptional: '',
    customTagCheckboxTerms: [],
  };
  const { setCustomTag } = useContext(CustomTagContext);
  const bottleRef = useRef<HTMLDivElement>(null);
  const customTagRef = useRef<HTMLDivElement>(null);
  const halfBottleRef = useRef<HTMLDivElement>(null);
  const hasOfensiveWord = (value: string | undefined) => {
    if (loading) return true;

    let wasNotCensured = true;

    if (value) {
      const { wasCensured } = censureOfensiveWords(value, offensiveWords || []);
      wasNotCensured = !wasCensured;
    }

    return wasNotCensured;
  };

  const CustomTagSchema = object().shape({
    customTagNumLines: number().required(
      'Debes seleccionar el número de líneas',
    ),
    customTagLineOne: string()
      .when([IDS_FORM.step, IDS_FORM.numlines], {
        is: (step: number, numLines: number) => step === 1 && numLines >= 1,
        then: (schema) =>
          schema
            .required(GENERIC_FORMS_ERRORS.required)
            .max(11, 'La línea 1 no puede superar los 11 caracteres')
            .test('hasOfensiveWord', FORM_ERRORS.ofensiveWord, hasOfensiveWord),
      })
      .matches(
        /^[a-zA-ZÀ-ÿ0-9 _.-¿?¡!&\u00f1\u00d1]*$/,
        FORMS_ERROS.noValidChars,
      ),
    customTagLineTwo: string()
      .when([IDS_FORM.step, IDS_FORM.numlines], {
        is: (step: number, numLines: number) => step === 1 && numLines === 2,
        then: (schema) =>
          schema
            .required(GENERIC_FORMS_ERRORS.required)
            .max(11, 'La línea 2 no puede superar los 11 caracteres')
            .test('hasOfensiveWord', FORM_ERRORS.ofensiveWord, hasOfensiveWord),
      })
      .when([IDS_FORM.step, IDS_FORM.numlines], {
        is: (step: number, numLines: number) => step === 1 && numLines === 3,
        then: (schema) =>
          schema
            .required(GENERIC_FORMS_ERRORS.required)
            .max(5, 'La línea 2 no puede superar los 5 caracteres')
            .test('hasOfensiveWord', FORM_ERRORS.ofensiveWord, hasOfensiveWord),
      })
      .matches(
        /^[a-zA-ZÀ-ÿ0-9 _.-¿?¡!&\u00f1\u00d1]*$/,
        FORMS_ERROS.noValidChars,
      ),
    customTagLineThree: string()
      .when([IDS_FORM.step, IDS_FORM.numlines], {
        is: (step: number, numLines: number) => step === 1 && numLines === 3,
        then: (schema) =>
          schema
            .required(GENERIC_FORMS_ERRORS.required)
            .max(14, 'La línea 3 no puede superar los 14 caracteres')
            .test('hasOfensiveWord', FORM_ERRORS.ofensiveWord, hasOfensiveWord),
      })
      .matches(
        /^[a-zA-ZÀ-ÿ0-9 _.-¿?¡!&\u00f1\u00d1]*$/,
        FORMS_ERROS.noValidChars,
      ),
    customTagLineOptional: string()
      .when(IDS_FORM.step, {
        is: 2,
        then: (schema) =>
          schema
            .max(18, 'La línea opcional no puede superar los 18 caracteres')
            .test('hasOfensiveWord', FORM_ERRORS.ofensiveWord, hasOfensiveWord),
      })
      .matches(
        /^[a-zA-ZÀ-ÿ0-9 _.-¿?¡!&\u00f1\u00d1]*$/,
        FORMS_ERROS.noValidChars,
      ),
    // TODO: Ahora mismo se valida en cuanto pasas al paso 3
    // eso es porque tiene el valor por defecto y al ser multi paso se "ha tocado"
    // el campo en los pasos anteriores (Formik toca todos los campos en el submit)
    customTagCheckboxTerms: array().when(IDS_FORM.step, {
      is: 3,
      then: (schema) =>
        schema
          .required()
          .min(
            1,
            'Debes aceptar los términos y condiciones para realizar el pedido',
          ),
    }),
  });

  // TODO: Añadir tipos
  const onSubmit = async (values: any, formikBag: any) => {
    if (values.customTagStep !== 4) {
      formikBag.setSubmitting(false);
    } else {
      const html2canvas = (await import('html2canvas')).default;
      // prettier-ignore
      const lines = `${values[IDS_FORM.lineOne]} // ${values[IDS_FORM.lineTwo]} // ${values[IDS_FORM.lineThree]}`;
      let etiquetaimage = '';
      let etiquetacheckoutimage = '';

      try {
        if (bottleRef.current) {
          const canvas = await html2canvas(
            bottleRef.current as HTMLDivElement,
            {
              backgroundColor: null,
              scale: 1,
            },
          );

          etiquetaimage = canvas.toDataURL('image/png');
        }

        if (halfBottleRef.current) {
          halfBottleRef.current.style.display = 'block';
          const parent = halfBottleRef.current.parentElement;

          if (parent) {
            parent.style.display = 'block';
            parent.style.position = 'fixed';
            parent.style.zIndex = '-1';
            parent.style.top = '-3000px';
            parent.removeAttribute('hidden');
          }

          const canvas = await html2canvas(halfBottleRef.current, {
            backgroundColor: null,
            scale: 1,
          });

          etiquetacheckoutimage = canvas.toDataURL('image/png');

          if (parent) {
            parent.style.display = 'none';
            parent.style.position = '';
            parent.style.zIndex = '';
            parent.style.top = '';
            parent.setAttribute('hidden', 'true');
          }
        }
      } catch (error) {
        debug('Error creating label image:', error);
      }

      setCustomTag({
        etiqueta: lines,
        etiquetalabel: values[IDS_FORM.lineOptional],
        etiquetaimage,
        etiquetacheckoutimage,
      });

      setOpen(false);

      formikBag.setFieldValue(IDS_FORM.step, values[IDS_FORM.step] - 1);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(open) => setOpen(open)}
      background={background}
      noMaxWidthDesktop
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={CustomTagSchema}
        validateOnChange
        validateOnBlur
        validateOnMount
      >
        {({ errors, values, setFieldValue, isSubmitting }) => {
          const customTagView = (Component: any, className?: string) => {
            const getValue = (line: string, defaultValue: string) =>
              values[line] !== ''
                ? errors[line] === FORM_ERRORS.ofensiveWord
                  ? {
                      value: censureOfensiveWords(
                        values[line],
                        offensiveWords || [],
                      ).censuredWord,
                      opacity: 1,
                    }
                  : {
                      value: values[line],
                      opacity: 1,
                    }
                : {
                    value: defaultValue,
                  };

            return (
              <Component
                {...(className && { className })}
                lineOne={getValue(IDS_FORM.lineOne, DEFAULT_VALUES.lineOne)}
                lineTwo={
                  values[IDS_FORM.numlines] >= 2
                    ? getValue(IDS_FORM.lineTwo, DEFAULT_VALUES.lineTwo)
                    : undefined
                }
                lineThree={
                  values[IDS_FORM.numlines] >= 3
                    ? getValue(IDS_FORM.lineThree, DEFAULT_VALUES.lineThree)
                    : undefined
                }
                lineOptional={getValue(
                  IDS_FORM.lineOptional,
                  DEFAULT_VALUES.lineOptional,
                )}
              />
            );
          };

          const customTagExport = (Component: any, className?: string) => (
            <Component
              {...(className && { className })}
              lineOne={
                values[IDS_FORM.lineOne] !== ''
                  ? { value: values[IDS_FORM.lineOne], opacity: 1 }
                  : undefined
              }
              lineTwo={
                values[IDS_FORM.lineTwo] !== ''
                  ? { value: values[IDS_FORM.lineTwo], opacity: 1 }
                  : undefined
              }
              lineThree={
                values[IDS_FORM.lineThree] !== ''
                  ? { value: values[IDS_FORM.lineThree], opacity: 1 }
                  : undefined
              }
              lineOptional={
                values[IDS_FORM.lineOptional] !== ''
                  ? {
                      value: values[IDS_FORM.lineOptional],
                      opacity: 1,
                    }
                  : undefined
              }
            />
          );

          const customTagRender =
            values[IDS_FORM.step] >= 3 ? customTagExport : customTagView;

          return (
            <Form
              className="flex flex-row justify-center gap-8 lg:min-w-[950px]"
              noValidate
            >
              <div className="flex min-w-full flex-col gap-6 lg:w-[300px] lg:min-w-0">
                <StepForm
                  show={
                    values[IDS_FORM.step] === 1 || values[IDS_FORM.step] === 2
                  }
                  className="flex justify-center lg:justify-start"
                >
                  <span className="u-headline u-headline--h2 -mt-8 text-center lg:mt-0">
                    TU ETIQUETA
                  </span>
                </StepForm>

                <StepForm
                  show={
                    values[IDS_FORM.step] === 1 || values[IDS_FORM.step] === 2
                  }
                  className="lg:hidden"
                >
                  <div ref={customTagRef} id="custom-tag-export">
                    {customTagRender(CustomTag, 'mx-auto max-w-[250px] mb-4')}
                  </div>

                  <i className="u-body u-body--xs mx-auto block max-w-[250px] text-center lg:hidden">
                    * La imagen de la etiqueta es una simulación aproximada del
                    resultado final.
                  </i>
                </StepForm>

                <StepForm show={values[IDS_FORM.step] === 1}>
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-row items-center justify-center gap-4 lg:justify-start">
                      <span>Nº de Líneas</span>

                      <Radio
                        className="flex flex-row gap-4"
                        id={IDS_FORM.numlines}
                        name={IDS_FORM.numlines}
                        label="Nº de Líneas"
                        radioClassName="!mb-0"
                        testid="number-of-lines"
                        values={[
                          { label: 1, value: 1 },
                          { label: 2, value: 2 },
                          { label: 3, value: 3 },
                        ]}
                      />
                    </div>
                    <div className="flex flex-col gap-4">
                      <Input
                        id={IDS_FORM.lineOne}
                        name={IDS_FORM.lineOne}
                        placeholder="Línea 1"
                        caption="Máximo 11 carácteres por línea."
                        error={errors[IDS_FORM.lineOne]}
                        maxLength={11}
                        testid="line1"
                      />

                      {values[IDS_FORM.numlines] === 2 && (
                        <Input
                          id={IDS_FORM.lineTwo}
                          name={IDS_FORM.lineTwo}
                          placeholder="Línea 2"
                          caption="Máximo 11 carácteres por línea."
                          error={errors[IDS_FORM.lineTwo]}
                          maxLength={11}
                          testid="line2"
                        />
                      )}

                      {values[IDS_FORM.numlines] === 3 && (
                        <Input
                          id={IDS_FORM.lineTwo}
                          name={IDS_FORM.lineTwo}
                          placeholder="Línea 2"
                          caption="Máximo 5 carácteres por línea."
                          error={errors[IDS_FORM.lineTwo]}
                          maxLength={5}
                          testid="line2"
                        />
                      )}

                      {values[IDS_FORM.numlines] >= 3 && (
                        <Input
                          id={IDS_FORM.lineThree}
                          name={IDS_FORM.lineThree}
                          placeholder="Línea 3"
                          caption="Máximo 14 carácteres por línea."
                          error={errors[IDS_FORM.lineThree]}
                          maxLength={14}
                          testid="line3"
                        />
                      )}
                    </div>
                  </div>
                </StepForm>

                <StepForm show={values[IDS_FORM.step] === 2}>
                  <Input
                    id={IDS_FORM.lineOptional}
                    name={IDS_FORM.lineOptional}
                    placeholder="Línea inferior (Opcional)"
                    caption="Máximo 18 carácteres por línea."
                    error={errors[IDS_FORM.lineOptional]}
                    maxLength={18}
                    testid="line-down"
                  />
                </StepForm>

                <StepForm
                  show={
                    values[IDS_FORM.step] === 1 || values[IDS_FORM.step] === 2
                  }
                >
                  <div className="u-body u-body--xs">
                    <div className="text-typo-secondary">Bases legales</div>
                    <p className="text-typo-tertiary">
                      No se admitirán textos con emoticonos, palabras
                      malsonantes, insultos,... Tampoco se admitirán el uso de
                      marcas comerciales. Bigcrafters se reserva la cancelación
                      de un pedido que incumpla esta norma.{' '}
                      <NextLink href={routes.contact}>
                        <a className="text-secondary-500">
                          Contacta con nosotros
                        </a>
                      </NextLink>
                    </p>
                  </div>
                </StepForm>

                <StepForm
                  show={
                    values[IDS_FORM.step] === 3 || values[IDS_FORM.step] === 4
                  }
                  className="lg:-mt-8"
                >
                  <div className="mb-2 flex flex-col justify-center gap-6">
                    <span className="u-headline u-headline--h2 order-2 text-center lg:order-1">
                      Esta es TU ETIQUETA
                    </span>

                    <div
                      className="relative order-1 mx-auto max-w-[230px] flex-shrink-0 lg:order-2"
                      ref={bottleRef}
                      id="custom-tag-bottle-export"
                    >
                      {window.innerWidth < 1024 ? (
                        <img
                          alt="Etiqueta personalizada"
                          src="/assets/images/fondoTag.png"
                        />
                      ) : (
                        <img
                          src="/assets/images/custom-tag-bottle.png"
                          alt="Etiqueta Personalizada botella completa"
                        />
                      )}

                      {window.innerWidth < 1024
                        ? customTagRender(
                            CustomTag,
                            'mx-auto max-w-[250px] mb-4',
                          )
                        : customTagRender(CustomTagBottle, 'absolute inset-0')}
                    </div>
                  </div>

                  <Checkbox
                    id={IDS_FORM.terms}
                    name={IDS_FORM.terms}
                    error={errors[IDS_FORM.terms]}
                    testid="accept-terms"
                    values={[
                      {
                        value: DEFAULT_VALUES.terms,
                        label:
                          'Acepto el pedido de este producto con la etiqueta personalizada. No se aceptan devoluciones.',
                      },
                    ]}
                  />
                </StepForm>

                <div className="flex flex-row gap-4">
                  <Button
                    variant="secondary"
                    size="large"
                    className="w-full !max-w-none"
                    disabled={values[IDS_FORM.step] === 1}
                    type="submit"
                    testid="last"
                    onClick={() =>
                      setFieldValue(IDS_FORM.step, values[IDS_FORM.step] - 1)
                    }
                  >
                    {values[IDS_FORM.step] === 3 || values[IDS_FORM.step] === 4
                      ? 'Editar'
                      : 'Anterior'}
                  </Button>

                  <Button
                    variant="tertiary"
                    size="large"
                    className="w-full !max-w-none"
                    type="submit"
                    loading={isSubmitting}
                    testid="next"
                    disabled={
                      !isEmptyObject(errors) ||
                      (values[IDS_FORM.step] === 3 &&
                        !values[IDS_FORM.terms].includes(
                          DEFAULT_VALUES.terms,
                        )) ||
                      isSubmitting
                    }
                    onClick={() => {
                      if (values[IDS_FORM.step] === 2) {
                        if (values[IDS_FORM.numlines] === 1) {
                          setFieldValue(IDS_FORM.lineTwo, '');
                          setFieldValue(IDS_FORM.lineThree, '');
                        } else if (values[IDS_FORM.numlines] === 2) {
                          setFieldValue(IDS_FORM.lineThree, '');
                        }
                      }
                      if (isEmptyObject(errors)) {
                        setFieldValue(IDS_FORM.step, values[IDS_FORM.step] + 1);
                      }
                    }}
                  >
                    {values[IDS_FORM.step] === 3 || values[IDS_FORM.step] === 4
                      ? 'Comprar'
                      : 'Siguiente'}
                  </Button>
                </div>
              </div>

              <StepForm
                show={
                  values[IDS_FORM.step] === 1 || values[IDS_FORM.step] === 2
                }
                className="hidden lg:block"
              >
                <div
                  className="relative -mt-[4.5rem] mb-4 flex-shrink-0"
                  ref={halfBottleRef}
                  id="custom-tag-half-bottle-export"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/assets/images/custom-tag-half-bottle.png"
                    alt="Etiqueta Personalizada"
                  />

                  {customTagRender(CustomTagHalfBottle, 'absolute inset-0')}
                </div>

                <i className="u-body u-body--xs mx-auto block max-w-[250px] text-center">
                  * La imagen de la etiqueta es una simulación aproximada del
                  resultado final.
                </i>
              </StepForm>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
